import React, { ChangeEvent, FC, useEffect, useRef } from "react";
import { useLayout } from "../../hooks/use-layout";

import { Button, ButtonVariant, FaIcon, FaIcons, InputField, Select, Theme, listItem } from "@in/component-library";
import { AccountV1 } from "../../models/account-v1";

interface CountryCodeOptionProps {
  value: string;
  text: string;
}

interface AddAccountProps {
  onClose: () => void;
  submit: () => void;
  isSaving: boolean;
  emailError?: string;
  phoneError?: string;
  selectedAccount?: listItem;
  updateEmail: (e: ChangeEvent<HTMLInputElement>) => void;
  updatePhoneNumber: (e: ChangeEvent<HTMLInputElement>) => void;
  updateCountryCode: (code: string) => void;
  countryCodeOptions: CountryCodeOptionProps[];
  email: string;
  phoneNumber: string;
  countryCode: string;
  account?: AccountV1;
  isPersonal?: boolean;
}

const AddAccount: FC<AddAccountProps> = ({
  onClose,
  submit,
  isSaving,
  emailError,
  phoneError,
  selectedAccount,
  updateEmail,
  updatePhoneNumber,
  updateCountryCode,
  countryCodeOptions,
  email,
  phoneNumber,
  countryCode,
  account,
  isPersonal
}) => {
  const { translations } = useLayout();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (emailError) {
      emailInputRef.current?.focus();
    } else if (phoneError) {
      phoneInputRef.current?.focus();
    }
  }, [emailError, phoneError]);

  return (
    <>
      <div className="add-account__account-wrapper">
        {isPersonal && account ? (
          <>
            <div className="add-account__account-icon">
              <FaIcon name={FaIcons.UserLight} />
            </div>
            <div className="add-account__account-info">
              <p>{account.name}</p>
              <p>{translations.chooseAccountPersonalText}</p>
              <p>{account.accountDisplayId}</p>
            </div>
          </>
        ) : (
          <>
            <div className="add-account__account-icon">
              <FaIcon name={FaIcons.BuildingLight} />
            </div>
            <div className="add-account__account-info">
              <p>{selectedAccount?.name}</p>
              <p>{selectedAccount?.address}</p>
              <p>{selectedAccount?.organizationNumber.replace(/^(.{3})(.{3})(.*)$/, "$1 $2 $3")}</p>
            </div>
          </>
        )}
      </div>

      {isPersonal ? (
        <p>{translations.editPersonalContactExplanatoryText}</p>
      ) : (
        <p>{translations.editContactExplanatoryText}</p>
      )}

      <InputField
        aria-live="assertive"
        className="data-hj-suppress add-account__email-input"
        name="email"
        label={translations.chooseAccountEmailLabel}
        onChange={updateEmail}
        value={email}
        errorMsg={emailError}
        ref={emailInputRef}
      />
      <div className="add-account__phone">
        <Select
          className="data-hj-suppress"
          name="countryCode"
          label={translations.chooseAccountCountryCodeLabel}
          placeholder={translations.countryCodeLabel}
          value={countryCode}
          onChange={updateCountryCode}
          options={countryCodeOptions}
        />
        <InputField
          aria-live="assertive"
          className="data-hj-suppress"
          type="tel"
          name="mobile"
          label={translations.chooseAccountPhoneNumberLabel}
          onChange={updatePhoneNumber}
          autoComplete="tel-national"
          value={phoneNumber}
          errorMsg={phoneError}
          ref={phoneInputRef}
        />
      </div>
      <div className="add-account__actions">
        <Button
          type="submit"
          variant={ButtonVariant.Solid}
          theme={Theme.Neutral}
          onClick={submit}
          isLoading={isSaving}
          disabled={isSaving}
        >
          {translations.chooseAccountNextLabel}
        </Button>
        <Button type="button" variant={ButtonVariant.Outlined} theme={Theme.Neutral} onClick={onClose}>
          {translations.personalOrExternalAccountsCancelButton}
        </Button>
      </div>
    </>
  );
};

export default AddAccount;
